import { isEqual } from '@Sage/ssm-utils';

export const NOTIFICATION_CHANNEL_IDS = ['email', 'push', 'inApp'];

export const NOTIFICATION_ENTITY_IDS = ['accounts', 'opportunities', 'calendars', 'activities'];

export const REMOVED_NOTIFICATION_ENTITY_IDS = ['contacts'];

export const NOTIFICATION_CHANNELS = [
    {
        id: 'email',
        icon: 'email',
        labelKey: 'label_automations_notifications_email',
        descKey: 'label_automations_notifications_email_desc',
        infoKey: 'label_automations_notifications_email_info',
    },
    {
        id: 'push',
        icon: 'smartPhone',
        labelKey: 'label_automations_notifications_mobile',
        descKey: 'label_automations_notifications_mobile_desc',
        infoKey: 'label_automations_notifications_mobile_info',
    },
    {
        id: 'inApp',
        icon: 'bellEnable',
        labelKey: 'label_automations_notifications_inapp',
        descKey: 'label_automations_notifications_inapp_desc',
        infoKey: 'label_automations_notifications_inapp_info',
    },
];

export const NOTIFICATION_ENTITIES = [
    { id: 'accounts', labelKey: 'label_accounts' },
    { id: 'opportunities', labelKey: 'label_opportunities' },
    { id: 'calendars', labelKeys: ['title_events', 'label_and', 'label_tasks'] },
    { id: 'activities', labelKey: 'title_activities' },
];

function createChannelEntities(value = true) {
    return NOTIFICATION_ENTITY_IDS.reduce((entities, entityId) => {
        entities[entityId] = value;
        return entities;
    }, {});
}

export function createDefaultNotificationsConfig() {
    return NOTIFICATION_CHANNEL_IDS.reduce((config, channelId) => {
        config[channelId] = createChannelEntities(true);
        return config;
    }, {});
}

function hasNotificationChannels(payload) {
    return (
        payload &&
        typeof payload === 'object' &&
        NOTIFICATION_CHANNEL_IDS.some((id) => payload[id] && typeof payload[id] === 'object')
    );
}

function unwrapNotificationsConfig(payload) {
    if (payload == null) return payload;
    if (typeof payload === 'string') {
        try {
            return unwrapNotificationsConfig(JSON.parse(payload));
        } catch {
            return null;
        }
    }
    if (typeof payload !== 'object') return payload;
    if (hasNotificationChannels(payload)) {
        return payload;
    }
    if (payload.data != null) return unwrapNotificationsConfig(payload.data);
    if (payload.result != null) return unwrapNotificationsConfig(payload.result);
    if (payload.PorDef != null) return unwrapNotificationsConfig(payload.PorDef);
    return payload;
}

export function normalizeNotificationsConfig(config) {
    const next = createDefaultNotificationsConfig();
    const source = unwrapNotificationsConfig(config);
    if (!source) return next;

    Object.keys(source).forEach((channelId) => {
        const channel = source[channelId];
        if (!channel || typeof channel !== 'object') return;
        if (!next[channelId]) next[channelId] = {};
        Object.keys(channel).forEach((entityId) => {
            if (REMOVED_NOTIFICATION_ENTITY_IDS.includes(entityId)) return;
            if (typeof channel[entityId] === 'boolean') {
                next[channelId][entityId] = channel[entityId];
            }
        });
    });

    return next;
}

export function resolveNotificationsConfig(payload, fallback) {
    const source = unwrapNotificationsConfig(payload);
    return normalizeNotificationsConfig(hasNotificationChannels(source) ? source : fallback);
}

export function getChannelCheckState(config, channelId) {
    const enabledCount = NOTIFICATION_ENTITY_IDS.filter(
        (entityId) => config?.[channelId]?.[entityId],
    ).length;
    const total = NOTIFICATION_ENTITY_IDS.length;

    return {
        checked: enabledCount > 0,
        indeterminate: enabledCount > 0 && enabledCount < total,
    };
}

export function toggleChannel(config, channelId) {
    const next = normalizeNotificationsConfig(config);
    if (!next[channelId]) return next;

    const { checked, indeterminate } = getChannelCheckState(next, channelId);
    const value = !(checked && !indeterminate);

    NOTIFICATION_ENTITY_IDS.forEach((entityId) => {
        next[channelId][entityId] = value;
    });

    return next;
}

export function toggleEntity(config, channelId, entityId) {
    const next = normalizeNotificationsConfig(config);
    if (typeof next[channelId]?.[entityId] !== 'boolean') return next;
    next[channelId][entityId] = !next[channelId][entityId];
    return next;
}

export function isNotificationsConfigDirty(draft, saved) {
    return !isEqual(draft, saved);
}

export function getNotificationEntityLabel(entity, getLiteral) {
    if (entity.labelKeys?.length) {
        return entity.labelKeys.map(getLiteral).join(' ');
    }
    return getLiteral(entity.labelKey);
}
